import { useState, useCallback } from "react";

export interface recorderControls {
  startRecording: () => void;
  stopRecording: () => void;
  togglePauseResume: () => void;
  recordingBlob?: Blob;
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  mediaRecorder?: MediaRecorder;
}

const MAX_RECORDING_TIME = 5 * 60 * 1000;

const useAudioRecorder = (
  onNotAllowedOrFound?: (exception: DOMException) => any,
  mediaRecorderOptions?: MediaRecorderOptions,
): recorderControls => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder>();
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout>();
  const [recordingBlob, setRecordingBlob] = useState<Blob>();

  const _startTimer = useCallback(() => {
    setRecordingTime(0);
    const interval = setInterval(() => {
      setRecordingTime((time) => {
        if (time + 1 >= MAX_RECORDING_TIME / 1000) {
          stopRecording();
          return time;
        }
        return time + 1;
      });
    }, 1000);
    setTimerInterval(interval);
  }, [setRecordingTime, setTimerInterval]);

  const _stopTimer = useCallback(() => {
    timerInterval && clearInterval(timerInterval);
    setTimerInterval(undefined);
  }, [timerInterval]);

  const startRecording = useCallback(() => {
    if (timerInterval != null) return;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        setIsRecording(true);
        const chunks: Blob[] = [];

        // iOS compatible MIME type detection
        const isIOS =
          /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const supportedMimeTypes = ["audio/mp4", "audio/webm", "audio/aac"];
        let selectedMimeType = mediaRecorderOptions?.mimeType;

        if (!selectedMimeType && isIOS) {
          for (const mimeType of supportedMimeTypes) {
            if (MediaRecorder.isTypeSupported(mimeType)) {
              selectedMimeType = mimeType;
              break;
            }
          }
        }

        const options = selectedMimeType
          ? { ...mediaRecorderOptions, mimeType: selectedMimeType }
          : mediaRecorderOptions;

        const recorder = new MediaRecorder(stream, options);
        setMediaRecorder(recorder);
        recorder.start();
        _startTimer();

        recorder.addEventListener("dataavailable", (event) => {
          chunks.push(event.data);
        });

        recorder.addEventListener("stop", () => {
          const blob = new Blob(chunks, {
            type: options?.mimeType || "audio/webm",
          });
          setRecordingBlob(blob);
          recorder.stream.getTracks().forEach((t) => t.stop());
          setMediaRecorder(undefined);
        });
      })
      .catch((error) => {
        onNotAllowedOrFound?.(error);
        alert(
          "Microphone access denied. Please enable it in your browser settings.",
        );
      });
  }, [timerInterval, mediaRecorderOptions, onNotAllowedOrFound, _startTimer]);

  const stopRecording = useCallback(() => {
    mediaRecorder?.stop();
    _stopTimer();
    setIsRecording(false);
    setIsPaused(false);
  }, [mediaRecorder, _stopTimer]);

  const togglePauseResume = useCallback(() => {
    if (isPaused) {
      setIsPaused(false);
      mediaRecorder?.resume();
      _startTimer();
    } else {
      setIsPaused(true);
      _stopTimer();
      mediaRecorder?.pause();
    }
  }, [isPaused, mediaRecorder, _startTimer, _stopTimer]);

  return {
    startRecording,
    stopRecording,
    togglePauseResume,
    recordingBlob,
    isRecording,
    isPaused,
    recordingTime,
    mediaRecorder,
  };
};

export default useAudioRecorder;
